export type StripeAccountStatus =
  | 'NotCreated'
  | 'Pending'
  | 'RequiresInformation'
  | 'UnderReview'
  | 'Verified'
  | 'Restricted'
  | 'Disabled';

// ── Stripe Connected Account (Contractors) ───────────────────────────

export interface CreateStripeAccountRequest {
  ssn?: string;
  dateOfBirth?: string;
}

export interface StripeConnectedAccount {
  hasAccount: boolean;
  accountId: string;
  status: StripeAccountStatus;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  onboardingCompleted: boolean;
  /** Whether Stripe has verified the individual's tax/identity data (SSN, address, attestation) — independent of bank linking and account restriction. */
  taxInfoVerified: boolean;
  /** Whether the connected account already has a payout destination (bank account) linked in Stripe. */
  hasBankAccount: boolean;
  onboardingUrl: string | null;
  currentlyDue: string[];
  eventuallyDue: string[];
  pastDue: string[];
  disabledReason: string | null;
}

// ── Stripe Onboarding Link ──────────────────────────────────────────

export interface GenerateOnboardingLinkRequest {
  returnUrl: string; // required, format: url, must match allowed domains
  refreshUrl: string; // required, format: url, must match allowed domains
}

export interface GenerateOnboardingLinkResponse {
  onboardingUrl: string;
}

// ── Stripe External Bank Account ─────────────────────────────────────

export interface AddExternalBankAccountRequest {
  bankAccountId: string; // required, GUID
}

// ── Stripe Tax Forms (1099, Connect embedded components) ─────────────

export interface TaxFormsSessionResponse {
  clientSecret: string;
  expiresAt: string; // ISO 8601 datetime
}

// ── Stripe Payment Link ──────────────────────────────────────────────

export interface StripePaymentLink {
  paymentLinkId: string;
  url: string;
  amountInCents: number;
  amount: number; // computed: amountInCents / 100
  currency: string; // default: "usd"
  active: boolean;
}

// ── Stripe Payment Method (Facilities) ───────────────────────────────

export interface StripePaymentMethod {
  paymentMethodId: string;
  type: string; // "card" | "us_bank_account" | etc.
  cardBrand: string | null; // populated when type == "card"
  cardLast4: string | null; // populated when type == "card"
  cardExpMonth: number | null; // populated when type == "card"
  cardExpYear: number | null; // populated when type == "card"
  bankName: string | null; // populated when type == "us_bank_account"
  bankAccountLast4: string | null; // populated when type == "us_bank_account"
  bankAccountType: string | null; // populated when type == "us_bank_account"
  isDefault: boolean;
}

// ── Stripe Attach Payment Method ─────────────────────────────────────

export interface AttachPaymentMethodRequest {
  paymentMethodId: string; // required, regex: ^pm_[a-zA-Z0-9]+$
}

// ── Stripe Customer ──────────────────────────────────────────────────

export interface CreateStripeCustomerResponse {
  stripeCustomerId: string;
}

// ── Stripe Health ────────────────────────────────────────────────────

export interface StripeHealthResponse {
  status: 'healthy' | 'unhealthy';
  service: 'stripe';
  timestamp: string; // ISO 8601 datetime
  error?: string; // only present when unhealthy
}
